import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Leaf, RefreshCw, Flame, Zap, Heart, X, ChevronDown } from 'lucide-react';
import { useTranslation } from '../i18n';

// Objective definitions with their filter mappings
export const objectives = [
  {
    id: 'douceur',
    label: 'Reprendre en douceur',
    icon: Leaf,
    emoji: '🌱',
    color: 'green',
    description: 'Activités douces pour recommencer le sport',
    levels: ['debutant', 'tous'],
    categories: ['yoga', 'pilates', 'mobilite', 'stretching'],
    priorityCategories: ['yoga', 'pilates']
  },
  {
    id: 'regulier',
    label: 'Bouger régulièrement',
    icon: RefreshCw,
    emoji: '🔄',
    color: 'blue',
    description: 'Un mix varié pour une routine équilibrée',
    levels: ['debutant', 'intermediaire', 'tous'],
    categories: [], // All categories
    priorityCategories: []
  },
  {
    id: 'challenge',
    label: 'Me challenger',
    icon: Flame,
    emoji: '🔥',
    color: 'orange',
    description: 'Séances intenses pour se dépasser',
    levels: ['intermediaire', 'avance'],
    categories: ['cardio', 'boxe', 'renforcement', 'outdoor'],
    priorityCategories: ['cardio', 'boxe']
  },
  {
    id: 'jeune',
    label: 'Progresser jeune',
    icon: Zap,
    emoji: '⚡',
    color: 'purple',
    description: 'Énergie et dynamisme pour les plus jeunes',
    levels: ['debutant', 'intermediaire', 'avance', 'tous'],
    categories: ['cardio', 'danse', 'outdoor', 'renforcement', 'boxe'],
    priorityCategories: ['danse', 'cardio']
  },
  {
    id: 'senior',
    label: 'Rester actif 50+',
    icon: Heart,
    emoji: '🌿',
    color: 'teal',
    description: 'Activités adaptées pour rester en forme',
    levels: ['debutant', 'intermediaire', 'tous'],
    categories: ['yoga', 'pilates', 'mobilite', 'outdoor'],
    priorityCategories: ['yoga', 'mobilite']
  }
];

// Get objective by ID
export const getObjectiveById = (id) => objectives.find(o => o.id === id) || null;

// Filter sessions based on selected objective
export const filterSessionsByObjective = (sessions, objectiveId) => {
  if (!objectiveId || objectiveId === 'all') return sessions;
  
  const objective = getObjectiveById(objectiveId);
  if (!objective) return sessions;
  
  return sessions.filter(session => {
    // Check level match
    const levelMatch = objective.levels.length === 0 || 
      objective.levels.includes(session.level) || 
      session.level === 'tous';
    
    // Check category match
    const categoryMatch = objective.categories.length === 0 || 
      objective.categories.includes(session.category?.toLowerCase());
    
    return levelMatch && categoryMatch;
  });
};

// Sort sessions by objective priority (priority categories first)
export const sortSessionsByObjective = (sessions, objectiveId) => {
  if (!objectiveId || objectiveId === 'all') return sessions;
  
  const objective = getObjectiveById(objectiveId);
  if (!objective || objective.priorityCategories.length === 0) return sessions;
  
  return [...sessions].sort((a, b) => {
    const aIsPriority = objective.priorityCategories.includes(a.category?.toLowerCase());
    const bIsPriority = objective.priorityCategories.includes(b.category?.toLowerCase());
    
    if (aIsPriority && !bIsPriority) return -1;
    if (!aIsPriority && bIsPriority) return 1;
    return 0;
  });
};

// LocalStorage key for persisting objective
const STORAGE_KEY = 'motivya_user_objective';

export default function ObjectiveSelector({ onObjectiveChange, initialObjective, className = '' }) {
  const { t } = useTranslation();
  const [selectedObjective, setSelectedObjective] = useState(initialObjective || null);
  const [isExpanded, setIsExpanded] = useState(!initialObjective);
  const [hasInteracted, setHasInteracted] = useState(!!initialObjective);

  // Sync with initialObjective prop when it changes
  useEffect(() => {
    if (initialObjective && initialObjective !== selectedObjective) {
      setSelectedObjective(initialObjective);
      setIsExpanded(false);
      setHasInteracted(true);
      localStorage.setItem(STORAGE_KEY, initialObjective);
    }
  }, [initialObjective]);

  // Load saved objective from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && !initialObjective) {
      setSelectedObjective(saved);
      setIsExpanded(false);
      setHasInteracted(true);
      onObjectiveChange?.(saved);
    }
  }, []);

  const handleSelect = (objectiveId) => {
    setSelectedObjective(objectiveId);
    setHasInteracted(true);
    localStorage.setItem(STORAGE_KEY, objectiveId);
    onObjectiveChange?.(objectiveId);
    
    // Collapse after selection with a small delay
    setTimeout(() => setIsExpanded(false), 300);
  };

  const handleClear = () => {
    setSelectedObjective(null);
    localStorage.removeItem(STORAGE_KEY);
    onObjectiveChange?.(null);
    setIsExpanded(true);
  };

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const selectedObj = getObjectiveById(selectedObjective);

  const colorClasses = {
    green: {
      bg: 'bg-green-500/10',
      border: 'border-green-500/30',
      text: 'text-green-400',
      selected: 'bg-green-500/20 border-green-500/50 ring-2 ring-green-500/30'
    },
    blue: {
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/30',
      text: 'text-blue-400',
      selected: 'bg-blue-500/20 border-blue-500/50 ring-2 ring-blue-500/30'
    },
    orange: {
      bg: 'bg-orange-500/10',
      border: 'border-orange-500/30',
      text: 'text-orange-400',
      selected: 'bg-orange-500/20 border-orange-500/50 ring-2 ring-orange-500/30'
    },
    purple: {
      bg: 'bg-purple-500/10',
      border: 'border-purple-500/30',
      text: 'text-purple-400',
      selected: 'bg-purple-500/20 border-purple-500/50 ring-2 ring-purple-500/30'
    },
    teal: {
      bg: 'bg-teal-500/10',
      border: 'border-teal-500/30',
      text: 'text-teal-400',
      selected: 'bg-teal-500/20 border-teal-500/50 ring-2 ring-teal-500/30'
    }
  };

  return (
    <div className={`${className}`}>
      {/* Header with question */}
      <div 
        className="flex items-center justify-between cursor-pointer mb-4"
        onClick={handleToggleExpand}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">{t('explore.objectiveTitle')}</h3>
            {selectedObj && !isExpanded && (
              <p className={`text-sm ${colorClasses[selectedObj.color].text}`}>
                {selectedObj.emoji} {t(`objectives.${selectedObj.id}`)}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selectedObjective && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="p-2 rounded-lg hover:bg-white/10 text-white/50 hover:text-white transition-colors"
              data-testid="clear-objective"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            className="text-white/50"
          >
            <ChevronDown className="w-5 h-5" />
          </motion.div>
        </div>
      </div>

      {/* Objective options */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {objectives.map((objective) => {
                const isSelected = selectedObjective === objective.id;
                const colors = colorClasses[objective.color];
                const Icon = objective.icon;

                return (
                  <motion.button
                    key={objective.id}
                    onClick={() => handleSelect(objective.id)}
                    className={`relative p-4 rounded-xl border transition-all text-left ${
                      isSelected 
                        ? colors.selected 
                        : `${colors.bg} ${colors.border} hover:border-white/30`
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid={`objective-${objective.id}`}
                  >
                    <div className="flex flex-col gap-2">
                      <div className={`text-2xl`}>{objective.emoji}</div>
                      <div className={`font-medium text-sm leading-tight ${isSelected ? colors.text : ''}`}>
                        {t(`objectives.${objective.id}`)}
                      </div>
                    </div>
                    {isSelected && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className={`absolute top-2 right-2 w-5 h-5 rounded-full ${colors.text} bg-current/20 flex items-center justify-center`}
                      >
                        <span className="text-xs">✓</span>
                      </motion.div>
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* Helper text */}
            {!hasInteracted && (
              <p className="text-center text-white/40 text-sm mt-4">
                {t('explore.objectiveTitle')}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Selected objective summary (when collapsed) */}
      {!isExpanded && selectedObj && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`p-3 rounded-xl ${colorClasses[selectedObj.color].bg} ${colorClasses[selectedObj.color].border} border`}
        >
          <p className="text-sm text-white/70">
            <span className={colorClasses[selectedObj.color].text}>{t(`objectives.${selectedObj.id}Desc`)}</span>
            {' '} • {t('objectives.clickToChange')}
          </p>
        </motion.div>
      )}
    </div>
  );
}
